import React, { useState, useEffect } from 'react';
import { FileText, Trash2, Eye, MapPin, Droplets } from 'lucide-react';
import { getProjects, deleteProject, Project } from '../services/api';

interface ProjectsPageProps {
  onViewDetails: (projectId: string) => void;
  onNavigateToUpload: () => void;
}

// Helper functions for formatting
const formatStatus = (status?: string): string => {
  const statusMap: Record<string, string> = {
    clean: 'Clean',
    stage1: 'Stage 1 Flagged',
    stage2: 'Stage 2 Flagged',
  };
  return statusMap[status || ''] || status || '-';
};

const formatCategory = (category?: string): string => {
  const categoryMap: Record<string, string> = {
    reforestation: 'Reforestation',
    wetland: 'Wetland Restoration',
    efficiency: 'Water Efficiency',
    infrastructure: 'Infrastructure',
  };
  return categoryMap[category || ''] || category || '-';
};

const ProjectsPage: React.FC<ProjectsPageProps> = ({ onViewDetails, onNavigateToUpload }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const projectsData = await getProjects();
      setProjects(projectsData);
    } catch (err) {
      setError('Failed to load projects');
      console.error('Error loading projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (projectId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      setError(null);
      await deleteProject(projectId);
      await loadProjects();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete project';
      console.error('Error deleting project:', err);
      setError(errorMessage);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="h-full w-full px-4 py-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading projects...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full w-full px-4 py-4 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <FileText className="w-12 h-12 mx-auto mb-2" />
            <p className="text-lg font-medium">Error Loading Projects</p>
          </div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadProjects}
            className="bg-cyan-600 hover:bg-cyan-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Calculate statistics
  const totalCount = projects.length;
  const cleanCount = projects.filter(p => p.status === 'clean').length;
  const stage1Count = projects.filter(p => p.status === 'stage1').length;
  const stage2Count = projects.filter(p => p.status === 'stage2').length;

  return (
    <div className="h-full w-full px-4 py-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Projects</h2>
          <p className="text-gray-600">Manage your saved VWBA analysis projects</p>
        </div>
        <button
          onClick={onNavigateToUpload}
          className="bg-cyan-600 hover:bg-cyan-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
        >
          Upload New Project
        </button>
      </div>

      {/* Statistics Cards */}
      {totalCount > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">{totalCount}</div>
            <div className="text-sm text-gray-500">Total Projects</div>
          </div>
          <div className="bg-white rounded-lg border border-green-200 p-4">
            <div className="text-2xl font-bold text-green-600">{cleanCount}</div>
            <div className="text-sm text-gray-500">Clean / Ready</div>
          </div>
          <div className="bg-white rounded-lg border border-amber-200 p-4">
            <div className="text-2xl font-bold text-amber-600">{stage1Count}</div>
            <div className="text-sm text-gray-500">Stage 1 Flagged</div>
          </div>
          <div className="bg-white rounded-lg border border-red-200 p-4">
            <div className="text-2xl font-bold text-red-600">{stage2Count}</div>
            <div className="text-sm text-gray-500">Stage 2 Flagged</div>
          </div>
        </div>
      )}

      {projects.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Projects Yet</h3>
          <p className="text-gray-600 mb-6">
            Upload and analyze VWBA fact sheets to create your first project.
          </p>
          <button
            onClick={onNavigateToUpload}
            className="bg-cyan-600 hover:bg-cyan-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Go to Upload
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" aria-label="Projects list">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project ID</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location/Basin</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Volumetric Water Benefit</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {projects.map((project) => (
                  <tr key={project.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{project.externalId || project.id}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate" title={project.name}>{project.name}</td>
                    <td className="px-4 py-3">
                      {project.type ? (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          project.type === 'new' ? 'bg-cyan-100 text-cyan-800' : 'bg-amber-100 text-amber-800'
                        }`}>
                          {project.type === 'new' ? 'New' : 'Historic'}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatCategory(project.category)}</td>
                    <td className="px-4 py-3">
                      {project.status ? (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          project.status === 'clean' ? 'bg-green-100 text-green-800' :
                          project.status === 'stage1' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {formatStatus(project.status)}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {project.basin ? (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-gray-400" />
                          <span className="truncate max-w-[120px]" title={project.basin}>{project.basin}</span>
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {project.volumetricWaterBenefit ? (
                        <span className="flex items-center gap-1">
                          <Droplets className="w-3 h-3 text-cyan-400" />
                          {project.volumetricWaterBenefit}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDate(project.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => onViewDetails(project.id)}
                          className="p-1.5 text-gray-500 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-colors"
                          aria-label={`View details for ${project.name}`}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" aria-hidden="true" />
                        </button>
                        <button
                          onClick={(e) => handleDeleteProject(project.id, e)}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          aria-label={`Delete ${project.name}`}
                          title="Delete Project"
                        >
                          <Trash2 className="w-4 h-4" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
